"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

declare const wvcClient: {
  getCurrentWPTermQueryParams: () => Record<string, any>;
};

type Language = "en" | "zh" | "fr" | "es" | "de";

const useWvcLanguage = (): Language => {
  const [lang, setLang] = React.useState<Language>("en");
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const htmlLang = document.documentElement.lang?.slice(0, 2)?.toLowerCase();
      if (htmlLang && ["en", "zh", "fr", "es", "de"].includes(htmlLang)) {
        setLang(htmlLang as Language);
      }
    }
  }, []);
  return lang;
};

const translations: Record<
  Language,
  {
    home: string;
    shop: string;
    collection: string;
    description: string;
    piece: string;
    pieces: string;
  }
> = {
  en: {
    home: "Home",
    shop: "Shop",
    collection: "Collection",
    description: "Refined silhouettes crafted with timeless tailoring and tactile fabrics.",
    piece: "piece",
    pieces: "pieces",
  },
  zh: {
    home: "主页",
    shop: "商店",
    collection: "系列",
    description: "用永恒的裁剪和触觉织物制作的精致剪裁。",
    piece: "件",
    pieces: "件",
  },
  fr: {
    home: "Accueil",
    shop: "Boutique",
    collection: "Collection",
    description: "Silhouettes raffinées créées avec une tailleur intemporelle et des tissus tactiles.",
    piece: "pièce",
    pieces: "pièces",
  },
  es: {
    home: "Inicio",
    shop: "Tienda",
    collection: "Colección",
    description: "Siluetas refinadas creadas con sastrería atemporal y telas táctiles.",
    piece: "pieza",
    pieces: "piezas",
  },
  de: {
    home: "Startseite",
    shop: "Shop",
    collection: "Kollektion",
    description: "Verfeinerte Silhouetten mit zeitlosem Schneidern und tastbaren Stoffen.",
    piece: "Teil",
    pieces: "Teile",
  },
};

function BreadcrumbsConsumer() {
  const language = useWvcLanguage();
  const t = translations[language];
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading || error || !links || links.length === 0) {
    return (
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/HomePage" className="text-muted-foreground hover:text-foreground">
                {t.home}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/ShopPage" className="text-muted-foreground hover:text-foreground">
                {t.shop}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-medium text-foreground">{t.collection}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        {links.map((link, index) => (
          <React.Fragment key={link.href || index}>
            <BreadcrumbItem>
              {index === lastIndex ? (
                <BreadcrumbPage className="font-medium text-foreground">
                  {link.title}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={link.href} className="text-muted-foreground hover:text-foreground">
                    {link.title}
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < lastIndex && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveHeaderConsumer() {
  const language = useWvcLanguage();
  const t = translations[language];
  const { terms } = useEcommerceTerms();

  const currentTerm = terms && terms.length > 0 ? terms[0] : null;
  const title = currentTerm?.name || t.collection;
  const description = currentTerm?.description || t.description;
  const totalCount = currentTerm?.count;

  return (
    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between border-b border-border pb-10">
      <div className="max-w-2xl">
        <div className="flex items-baseline gap-3">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-foreground">
            {title}
          </h1>
          {typeof totalCount === "number" && (
            <span className="text-sm font-sans text-muted-foreground tabular-nums">
              ({totalCount} {totalCount === 1 ? t.piece : t.pieces})
            </span>
          )}
        </div>
        {description && (
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            {description}
          </p>
        )}
      </div>

      {terms && terms.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 pt-2 md:pt-0">
          {terms.map((term, i) => (
            <div key={term.id} data-index={i}>
              <Link
                to={term.link || "#"}
                className={`inline-block px-3.5 py-1.5 text-xs uppercase tracking-wider transition-colors border ${
                  term.id === currentTerm?.id
                    ? "bg-secondary text-secondary-foreground border-secondary"
                    : "bg-background text-foreground border-border hover:border-foreground"
                }`}
              >
                {term.name}
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ArchiveTitle() {
  const queryParams =
    typeof window !== "undefined" && typeof wvcClient !== "undefined"
      ? wvcClient.getCurrentWPTermQueryParams()
      : { taxonomy: "product_cat" };

  const wp_term_query = new WP_Term_Query(queryParams);

  return (
    <section
      id="archive-title-section"
      data-nav="light"
      className="w-full bg-background text-foreground pt-8 pb-4 sm:pt-12 sm:pb-6"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ShopBreadcrumbsProvider>
          <BreadcrumbsConsumer />
        </ShopBreadcrumbsProvider>

        <EcommerceTermsProvider wp_term_query={wp_term_query}>
          <ArchiveHeaderConsumer />
        </EcommerceTermsProvider>
      </div>
    </section>
  );
}